import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

export type VaultThemePref = 'system' | 'light' | 'dark';
export type VaultTheme = 'light' | 'dark';

const STORAGE_KEY = 'basiqr-vault-theme';

// The vault theme that sheets and menus pick up. Outside the vault (the
// product page) nothing provides it, so they stay on the dark glass.
export const VaultThemeContext = createContext<VaultTheme>('dark');

// The theme colours the browser chrome uses around the page (mobile status bar).
// The cutting mat's base colour in each theme (#467).
export const VAULT_BACKGROUND: Record<VaultTheme, string> = { dark: '#284546', light: '#f4f9f9' };

/**
 * The page behind a shopper page (overscroll, the phone's status bar) follows
 * its theme: the body's background and <meta name="theme-color">, both put
 * back on the way out. `background` is a brand's mat colour (#462).
 */
export function useThemeChrome(theme: VaultTheme, background?: string) {
  useEffect(() => {
    const colour = background ?? VAULT_BACKGROUND[theme];
    const body = document.body.style;
    const previous = body.backgroundColor;
    body.backgroundColor = colour;
    let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const created = !meta;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    const previousMeta = meta.content;
    meta.content = colour;
    return () => {
      body.backgroundColor = previous;
      if (created) meta?.remove();
      else if (meta) meta.content = previousMeta;
    };
  }, [theme, background]);
}

const readPref = (): VaultThemePref => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === 'light' || saved === 'dark' || saved === 'system' ? saved : 'system';
  } catch {
    return 'system';
  }
};

const systemIsDark = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;

const reducedMotion = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

type ViewTransition = { ready: Promise<void>; finished: Promise<void>; skipTransition: () => void };

/**
 * The vault's own light/dark choice: follows the phone until you pick one.
 * The header button flips between light and dark (always a visible change);
 * Settings also offers "Auto". Kept apart from the brand workspace's theme
 * (ThemeContext), which is a different product with its own setting.
 */
export function useVaultTheme() {
  const [pref, setPrefState] = useState<VaultThemePref>(readPref);
  const [systemDark, setSystemDark] = useState(systemIsDark);

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  const theme: VaultTheme = pref === 'system' ? (systemDark ? 'dark' : 'light') : pref;

  // What the screen shows right now, read by quick repeated taps: during the
  // reveal animation the state update hasn't landed yet.
  const shown = useRef<VaultTheme>(theme);
  shown.current = theme;
  const running = useRef<ViewTransition | null>(null);

  const setPref = useCallback((next: VaultThemePref, origin?: { x: number; y: number }) => {
    shown.current = next === 'system' ? (systemIsDark() ? 'dark' : 'light') : next;
    const apply = () => {
      setPrefState(next);
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // private mode: the choice lasts for this visit only
      }
    };
    const doc = document as Document & { startViewTransition?: (cb: () => void) => ViewTransition };
    if (!origin || !doc.startViewTransition || reducedMotion()) {
      apply();
      return;
    }
    // A tap during the previous reveal: finish it at once, then grow the new one.
    running.current?.skipTransition();
    const root = document.documentElement;
    root.classList.add('v-theme-vt');
    const transition = doc.startViewTransition(() => flushSync(apply));
    running.current = transition;
    const radius = Math.hypot(Math.max(origin.x, innerWidth - origin.x), Math.max(origin.y, innerHeight - origin.y));
    transition.ready
      .then(() => {
        root.animate(
          { clipPath: [`circle(0px at ${origin.x}px ${origin.y}px)`, `circle(${radius}px at ${origin.x}px ${origin.y}px)`] },
          { duration: 560, easing: 'cubic-bezier(.3,.7,.2,1)', pseudoElement: '::view-transition-new(root)' }
        );
      })
      .catch(() => {});
    transition.finished.finally(() => {
      if (running.current !== transition) return;
      running.current = null;
      root.classList.remove('v-theme-vt');
    });
  }, []);

  // The header button: light ⇄ dark, from whatever is on screen.
  const toggle = useCallback(
    (origin?: { x: number; y: number }) => setPref(shown.current === 'dark' ? 'light' : 'dark', origin),
    [setPref]
  );

  return { pref, theme, setPref, toggle };
}
